import { useQuery } from "@tanstack/react-query"
import { getBalance, getReferralLink } from "@/shared/api"
import type { AuthMe, Balance, ReferralLinkResponse } from "@/shared/api/types"
import { useRoomBootstrapStore } from "@/shared/store"

export const userKeys = {
  all: ["user"] as const,
  me: () => [...userKeys.all, "me"] as const,
  balance: () => [...userKeys.all, "balance"] as const,
  wallet: () => [...userKeys.all, "wallet"] as const,
  referralLink: () => [...userKeys.all, "referral-link"] as const,
}

export const useUser = () => {
  return useQuery<AuthMe>({
    queryKey: userKeys.me(),
    queryFn: async () => {
      throw new Error("/v1/auth/me endpoint is not implemented")
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    // If auth is blocked by CORS/network, don't hammer.
    retry: false,
    enabled: false,
  })
}

export const useBalance = () => {
  const bootstrapReady = useRoomBootstrapStore((s) => s.bootstrapReady)
  return useQuery<Balance>({
    queryKey: userKeys.balance(),
    queryFn: getBalance,
    // Centrifugo WS is the primary live-update source; REST is fetched once on mount.
    // No polling — balance is pushed via subscribeBalance in PvpRoomRoot.
    staleTime: Infinity,
    // Prevent duplicate cold-start balance fetch before `/v1/bootstrap` seeds cache.
    enabled: bootstrapReady,
  })
}

export const useReferralLink = () => {
  return useQuery<ReferralLinkResponse>({
    queryKey: userKeys.referralLink(),
    queryFn: getReferralLink,
    staleTime: 1000 * 60 * 5,
  })
}

export const useTonWallet = () => {
  return useQuery<{ address?: string | null }>({
    queryKey: userKeys.wallet(),
    queryFn: async () => {
      throw new Error("/v1/ton-wallet endpoint was removed from API")
    },
    staleTime: 1000 * 60, // 1 minute
    enabled: false,
  })
}
